"use client";

import StarIcon from "public/next-public/common/icons/star-solid-icon.svg";
import React, { useState } from "react";
import styles from "./starRating.module.scss";

export type StarRatingSize = "xs" | "small" | "medium" | "large" | "xl";

interface StarRatingProps {
  rating?: number;
  size?: StarRatingSize;
  onChange?: (value: number) => void;
  onHoverChange?: (value: number | null) => void;
  "aria-label"?: string;
  className?: string;
}

const STAR_PX: Record<StarRatingSize, number> = {
  xs: 12,
  small: 16,
  medium: 24,
  large: 32,
  xl: 48,
};

const STAR_COUNT = 5;

const StarRating: React.FC<StarRatingProps> = ({
  rating = 0,
  size = "medium",
  onChange,
  onHoverChange,
  "aria-label": ariaLabel,
  className,
}) => {
  const dimension = STAR_PX[size];
  const interactive = typeof onChange === "function";
  const [hoverValue, setHoverValue] = useState<number | null>(null);

  const updateHover = (value: number | null): void => {
    setHoverValue(value);
    onHoverChange?.(value);
  };

  const fillFor = (slotIndex: number): number => {
    if (interactive && hoverValue !== null) {
      return slotIndex < hoverValue ? 1 : 0;
    }
    return Math.max(0, Math.min(1, rating - slotIndex));
  };

  const rootClasses = [styles.StarRating, className || ""].filter(Boolean).join(" ");

  if (interactive) {
    return (
      <div
        className={rootClasses}
        role="group"
        aria-label={ariaLabel ?? "Rate this item"}
        onMouseLeave={() => updateHover(null)}
      >
        {Array.from({ length: STAR_COUNT }, (_, i) => {
          const value = i + 1;
          return (
            <button
              key={i}
              type="button"
              className={styles.button}
              aria-label={`Rate ${value} out of ${STAR_COUNT}`}
              aria-pressed={rating >= value}
              onMouseEnter={() => updateHover(value)}
              onFocus={() => updateHover(value)}
              onBlur={() => updateHover(null)}
              onClick={() => onChange?.(value)}
            >
              <Star fill={fillFor(i)} dimension={dimension} />
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div className={rootClasses} role="img" aria-label={ariaLabel ?? `${rating.toFixed(1)} out of ${STAR_COUNT} stars`}>
      {Array.from({ length: STAR_COUNT }, (_, i) => (
        <Star key={i} fill={fillFor(i)} dimension={dimension} />
      ))}
    </div>
  );
};

interface StarProps {
  fill: number;
  dimension: number;
}

const Star: React.FC<StarProps> = ({ fill, dimension }) => (
  <span className={styles.star} style={{ width: dimension, height: dimension }}>
    <span className={styles.empty}>
      <StarIcon width={dimension} height={dimension} aria-hidden="true" />
    </span>
    {fill > 0 && (
      <span className={styles.fill} style={{ width: `${fill * 100}%` }}>
        <StarIcon width={dimension} height={dimension} aria-hidden="true" />
      </span>
    )}
  </span>
);

export default StarRating;
